import { OTP_GATE_COPY, OTP_GATE_LENGTH } from "../constants";
import type {
  AcceptMode,
  OtpEntry,
  OtpGateOptions,
  OtpGateView,
  OtpVerifyResult,
} from "../types";

export const shouldHoldForOtp = (
  gate: { required: boolean } | undefined,
  verified: boolean
): boolean => Boolean(gate?.required) && !verified;

export const emptyOtpDigits = (length: number = OTP_GATE_LENGTH): string[] =>
  Array.from({ length }, () => "");

export const otpCodeOf = (digits: string[]): string => digits.join("");

export const isOtpComplete = (digits: string[]): boolean =>
  digits.length > 0 && digits.every((digit) => /^[0-9]$/.test(digit));

const onlyDigits = (value: string): string => value.replace(/[^0-9]/g, "");

const withDigit = (digits: string[], index: number, digit: string): string[] =>
  digits.map((current, i) => (i === index ? digit : current));

const fillFromStart = (code: string, length: number): string[] =>
  Array.from({ length }, (_, i) => code[i] ?? "");

const typedOver = (text: string, previous: string): string => {
  if (!previous || text.length !== previous.length + 1) {
    return text;
  }
  if (text.startsWith(previous)) {
    return text.slice(previous.length);
  }
  if (text.endsWith(previous)) {
    return text.slice(0, text.length - previous.length);
  }
  return text;
};

const withoutPrevious = (
  code: string,
  previous: string,
  length: number
): string => {
  if (!previous || code.length <= length) {
    return code;
  }
  if (code.startsWith(previous)) {
    return code.slice(previous.length);
  }
  if (code.endsWith(previous)) {
    return code.slice(0, code.length - previous.length);
  }
  return code;
};

export const applyOtpText = (
  digits: string[],
  index: number,
  text: string
): OtpEntry | null => {
  const length = digits.length;
  const previous = digits[index] ?? "";

  if (text === "") {
    return { digits: withDigit(digits, index, ""), focus: null };
  }

  const typed = onlyDigits(typedOver(text, previous));
  if (!typed) {
    return null;
  }

  if (typed.length === 1) {
    return {
      digits: withDigit(digits, index, typed),
      focus: index + 1 < length ? index + 1 : null,
    };
  }

  const code = withoutPrevious(typed, previous, length).slice(0, length);
  return {
    digits: fillFromStart(code, length),
    focus: Math.min(code.length, length - 1),
  };
};

export const applyOtpBackspace = (
  digits: string[],
  index: number
): OtpEntry => {
  if (digits[index]) {
    return { digits: withDigit(digits, index, ""), focus: null };
  }
  if (index <= 0) {
    return { digits, focus: null };
  }
  return { digits: withDigit(digits, index - 1, ""), focus: index - 1 };
};

const settleVerify = async (
  gate: OtpGateOptions,
  code: string
): Promise<OtpVerifyResult> => {
  try {
    const result = await gate.onVerify(code);
    return result && result.ok ? result : { ok: false, message: result?.message };
  } catch {
    return { ok: false };
  }
};

export class OtpGateSession {
  private verified = false;
  private pendingMode: AcceptMode | null = null;
  private acceptAfterToken: AcceptMode | null = null;
  private tokenAtVerify: string | undefined = undefined;
  private current: OtpGateView = {
    digits: emptyOtpDigits(),
    busy: false,
    error: null,
  };

  get isLocked(): boolean {
    return this.pendingMode !== null;
  }

  get view(): OtpGateView {
    return this.current;
  }

  /** Whether an accept made now would be held for the code. */
  wouldHold(gate: OtpGateOptions | undefined): boolean {
    return shouldHoldForOtp(gate, this.verified);
  }

  hold(gate: OtpGateOptions | undefined, mode: AcceptMode): boolean {
    if (shouldHoldForOtp(gate, this.verified)) {
      this.pendingMode = mode;
      this.current = { ...this.current, error: null };
      return true;
    }
    this.acceptAfterToken = null;
    return false;
  }

  setDigits(digits: string[]): void {
    this.current = { ...this.current, digits };
  }

  async verify(
    gate: OtpGateOptions | undefined,
    token: string | undefined,
    onBusy: () => void,
    failedMessage: string = OTP_GATE_COPY.failed
  ): Promise<boolean> {
    if (
      !gate ||
      this.pendingMode === null ||
      this.current.busy ||
      !isOtpComplete(this.current.digits)
    ) {
      return false;
    }

    this.current = { ...this.current, busy: true, error: null };
    onBusy();

    const result = await settleVerify(gate, otpCodeOf(this.current.digits));

    if (!result.ok) {
      this.current = {
        digits: emptyOtpDigits(),
        busy: false,
        error: result.message ?? failedMessage,
      };
      return false;
    }

    const mode = this.pendingMode ?? "selected";
    this.verified = true;
    this.pendingMode = null;
    this.current = { digits: emptyOtpDigits(), busy: false, error: null };
    this.tokenAtVerify = token;
    this.acceptAfterToken = mode;
    return true;
  }

  dropStashedAccept(): void {
    this.acceptAfterToken = null;
  }

  takeStashedAccept(token: string | undefined): AcceptMode | null {
    if (this.acceptAfterToken === null || token === this.tokenAtVerify) {
      return null;
    }
    const mode = this.acceptAfterToken;
    this.acceptAfterToken = null;
    return mode;
  }
}
